import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileCheck,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  User,
  MapPin,
  Phone,
  Mail,
  Download,
  Loader2,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { useState } from 'react';
// Use client-side PDF generation to avoid ID collision issues
import { downloadCompletionPDF } from './CompletionPDF';
import { useToast } from '@/hooks/use-toast';

interface CompletionRecord {
  id: number;
  customerId: number | null;
  installationId: number | null;
  customerName: string;
  customerAddress: string;
  customerEmail: string | null;
  customerPhone: string | null;
  projectRefNumber: string;
  installationAddress: string;
  installationDescription: string;
  status: string;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
  signatureImage?: string;
  confirmationBuildingRegs?: boolean;
  manufacturersManuals?: boolean;
  serviceRequirements?: boolean;
  maintenanceRequirements?: boolean;
  performanceEstimates?: boolean;
  mcsCertificate?: boolean;
  mcsComplianceConfirmation?: boolean;
  customerComments?: string;
  signedByName?: string;
}

interface CompletionsListProps {
  records: CompletionRecord[];
  onSelect: (record: CompletionRecord) => void;
  emptyMessage?: string;
}

export function CompletionsList({
  records,
  onSelect,
  emptyMessage = 'No records found',
}: CompletionsListProps) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const { toast } = useToast();

  const handleDownloadPDF = async (e: React.MouseEvent, record: CompletionRecord) => {
    e.stopPropagation();
    setDownloadingId(record.id);

    try {
      // Use client-side React PDF generation to avoid server-side ID collision issues
      // This ensures we always generate the correct HIES completion PDF
      await downloadCompletionPDF(record, record.signatureImage);

      toast({
        title: 'Success',
        description: 'HIES Completion PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary' as const, icon: Clock },
      pending: { label: 'Pending', variant: 'secondary' as const, icon: AlertCircle },
      signed: { label: 'Signed', variant: 'success' as const, icon: CheckCircle2 },
      completed: { label: 'Completed', variant: 'default' as const, icon: FileCheck },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (records.length === 0) {
    return (
      <Card className="p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <FileCheck className="h-12 w-12 text-gray-400" />
          <p className="text-gray-600">{emptyMessage}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
      {records.map((record) => (
        <Card
          key={record.id}
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onSelect(record)}
        >
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {record.customerName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">Project: {record.projectRefNumber}</p>
              </div>
              {getStatusBadge(record.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Installation Address</p>
                  <p className="text-sm text-gray-600">{record.installationAddress}</p>
                </div>
              </div>

              {record.customerEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-600">{record.customerEmail}</p>
                </div>
              )}

              {record.customerPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-600">{record.customerPhone}</p>
                </div>
              )}

              <div className="pt-3 border-t flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {record.signedAt ? (
                    <span>Signed: {formatDate(record.signedAt)}</span>
                  ) : (
                    <span>Created: {formatDate(record.createdAt)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Show PDF download button only for signed forms */}
                  {record.status === 'signed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      onClick={(e) => handleDownloadPDF(e, record)}
                      disabled={downloadingId === record.id}
                    >
                      {downloadingId === record.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(record);
                    }}
                  >
                    Open
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
